import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const PaymentGateway = () => {
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get payment data from state or session storage
  const paymentData = location.state?.paymentData || 
    JSON.parse(sessionStorage.getItem('paymentData') || '{}');
  
  useEffect(() => {
    if (!paymentData || !paymentData.orderId) {
      setError('Invalid payment data. Please try again.');
      return;
    }
    
    // Simulate payment processing
    const timer = setTimeout(() => {
      setProcessing(false);
      
      // Store success data in session storage
      sessionStorage.setItem('paymentResult', JSON.stringify({
        success: true,
        orderId: paymentData.orderId,
        transactionId: 'TXN' + Math.floor(Math.random() * 1000000),
        paymentMethod: paymentData.paymentMethod,
        amount: paymentData.amount,
        timestamp: new Date().toISOString()
      }));
      
      // Auto-redirect to success page after 2 seconds
      setTimeout(() => {
        navigate('/payment/success', { 
          state: { 
            orderId: paymentData.orderId,
            paymentMethod: paymentData.paymentMethod 
          } 
        });
      }, 2000);
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [paymentData, navigate]);
  
  // Get gateway brand based on payment method
  const getGatewayBrand = () => {
    switch(paymentData.paymentMethod) {
      case 'payhere':
        return {
          name: 'PayHere',
          logo: '💳',
          color: 'bg-blue-500'
        };
      case '2checkout':
        return {
          name: '2Checkout',
          logo: '🔒',
          color: 'bg-purple-500'
        };
      default:
        return {
          name: 'Payment Gateway',
          logo: '💰',
          color: 'bg-green-500'
        };
    }
  };
  
  const gateway = getGatewayBrand();
  
  return (
    <div className="max-w-md mx-auto my-12">
      <div className="neumorph p-8 rounded-lg">
        <div className={`${gateway.color} text-white p-4 rounded-lg mb-6 text-center`}>
          <div className="text-4xl mb-2">{gateway.logo}</div>
          <h1 className="text-2xl font-bold">{gateway.name}</h1>
        </div>
        
        <div className="text-center">
          {processing ? (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800 mx-auto mb-4"></div>
              <p className="text-lg">Processing your payment...</p>
              <p className="text-sm text-gray-600 mt-2">Please do not close this window</p>
            </>
          ) : (
            <>
              <div className="text-5xl mb-4">✅</div>
              <h2 className="text-xl font-bold text-green-600 mb-2">Payment Successful!</h2>
              <p className="text-gray-600 mb-4">Your transaction has been completed</p>
              <p className="text-sm">Order ID: {paymentData.orderId}</p>
              <p className="text-sm">Amount: ${paymentData.amount?.toFixed(2)}</p>
              <p className="text-sm mb-6">Redirecting to confirmation page...</p>
            </>
          )}
        </div>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mt-6">
            {error}
          </div>
        )}
        
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>Secure payment powered by {gateway.name}</p>
          <p className="mt-2">All transactions are encrypted and secure</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentGateway;
